import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import RoleChip from 'components/chip/RoleChip';
import ToolbarPopover from 'components/layout/ToolbarPopover';
import { UserSession, getLoggedInAccounts } from 'stores/AuthenticationStoreAdapter';

type Props = {
  userSession: UserSession;
  /** ログアウト */
  onSignOut: () => void;
  /** 別アカウントに切り替える（sessionId を指定） */
  onSwitchAccount: (sessionId: string) => void;
  /** 別アカウントを追加ログインする（ログインページへ） */
  onAddAccount: () => void;
};

const ToolbarAccount: React.FC<Props> = ({
  userSession,
  onSignOut,
  onSwitchAccount,
  onAddAccount,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // ポップオーバーを開いた時点のアカウント一覧を取得
  const accounts = React.useMemo(() => (open ? getLoggedInAccounts() : []), [open]);
  const otherAccounts = accounts.filter((a) => !a.active);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const initial = userSession.userName ? userSession.userName[0] : '?';

  return (
    <>
      <IconButton onClick={handleClick} size="small" sx={{ ml: 2 }}>
        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', fontWeight: 600 }}>
          {initial}
        </Avatar>
      </IconButton>
      <ToolbarPopover open={open} anchorEl={anchorEl} onClose={handleClose} width={300}>
        {/* 現在のユーザー情報 */}
        <Box sx={{ px: 2, py: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ width: 40, height: 40, fontSize: '1rem', fontWeight: 600 }}>
              {initial}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {userSession.userName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {userSession.mailAddress}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
            {userSession.roles.map((role) => (
              <RoleChip key={role} role={role} />
            ))}
          </Stack>
        </Box>

        <Divider />

        {/* アカウント切り替え */}
        <Box sx={{ p: 1 }}>
          {otherAccounts.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', px: 1.5, pt: 0.5, pb: 0.5 }}
            >
              アカウントを切り替え
            </Typography>
          )}

          {otherAccounts.map((account) => (
            <Box
              key={account.sessionId}
              onClick={() => {
                handleClose();
                onSwitchAccount(account.sessionId);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', fontWeight: 600 }}>
                {account.userName ? account.userName[0] : '?'}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {account.userName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {account.mailAddress}
                </Typography>
              </Box>
            </Box>
          ))}

          <Button
            fullWidth
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => {
              handleClose();
              onAddAccount();
            }}
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary',
              borderRadius: 1.5,
              px: 1.5,
              py: 0.75,
              '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            別のアカウントを追加
          </Button>
        </Box>

        <Divider />

        {/* ログアウト */}
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            size="small"
            startIcon={<LogoutIcon sx={{ fontSize: 18 }} />}
            onClick={() => {
              handleClose();
              onSignOut();
            }}
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary',
              borderRadius: 1.5,
              px: 1.5,
              py: 0.75,
              '&:hover': { color: 'error.main', bgcolor: 'error.lighter' },
            }}
          >
            ログアウト
          </Button>
        </Box>
      </ToolbarPopover>
    </>
  );
};

export default ToolbarAccount;
